import { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { AppBar, Box, Toolbar } from '@mui/material';
import clsx from 'clsx';

import { requestUserAuthTokenExchange } from '@atom/actions/userActions';
import Logo from '@atom/components/common/navigation/Logo';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Icon, IconButton, Progress, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import layoutStyles from '@atom/styles/layout';
import { RootState } from '@atom/types/store';
import accountUtilities from '@atom/utilities/accountUtilities';
import api from '@atom/utilities/api';
import { BASE_API_ENDPOINT, TENANT_ENDPOINT } from '@atom/utilities/endpoints';

import GoogleSocialLoginButton from './GoogleSocialLoginButton';
import LoginFooter from './LoginFooter';
import ResetPasswordModal from './ResetPasswordModal';

import classes from './login.module.css';

const navBarStyle = {
  height: layoutStyles.topNavHeight,
  backgroundColor: colors.neutral.white,
  boxShadow: '0 2px 4px 1px',
  color: colors.utility.boxShadow,
  position: 'fixed',
  top: 0,
};

const styles = {
  passwordField: {
    paddingTop: '0.4rem',
  },
};

interface LoginForm {
  email: string;
  password: string;
}

interface Tenant {
  id: string;
  name: string;
  preferences: {
    authentication: {
      connections: Array<{
        type: string;
        name: string;
      }>;
    };
  };
}

const blankTenantData = {
  id: '',
  name: '',
  preferences: {
    authentication: {
      connections: [],
    },
  },
};

const activeDirectoryLoginUrl = connection =>
  `${BASE_API_ENDPOINT}/auth/social?connection=${encodeURIComponent(connection)}`;

const Login = () => {
  const [, { navigate }] = useNavigation();
  const [searchParams] = useSearchParams();
  const authError = searchParams.get('error') === 'auth_failed';
  const [isTenantLoading, setIsTenantLoading] = useState<boolean>(false);
  const [tenant, setTenant] = useState<Tenant>(blankTenantData);
  const [loginForm, setLoginForm] = useState<LoginForm>({
    email: '',
    password: '',
  });
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);
  const [emailPasswordError, setEmailPasswordError] = useState<boolean>(false);

  const dispatch = useDispatch();
  const isLoginLoading = useSelector(
    (state: RootState) => state.loading.loadingUserLogin,
  );

  const getTenant = async () => {
    const name = window.location.host.split('.')[1]
      ? window.location.host.split('.')[0]
      : '';

    if (name && name !== 'www') {
      setIsTenantLoading(true);

      const { data: tenantData } = await api
        .get(`${TENANT_ENDPOINT}/${name}`)
        .catch(() => ({ data: blankTenantData }));

      setIsTenantLoading(false);
      setTenant(tenantData);
    }
  };

  useEffect(() => {
    getTenant();
  }, []);

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setLoginForm(prevFormData => ({ ...prevFormData, [name]: value }));
  };

  const submit = async () => {
    try {
      const result = await accountUtilities.individualAccountLogin(
        loginForm.email,
        loginForm.password,
      );
      dispatch(
        requestUserAuthTokenExchange({
          accessToken: result.accessToken,
        }),
      );
    } catch (error) {
      setEmailPasswordError(true);
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      submit();
    }
  };

  const navigateToRegistration = () => {
    navigate('/registration');
  };

  const togglePasswordVisibleState = () => {
    setIsPasswordVisible(prevIsVisible => !prevIsVisible);
  };

  const connections = tenant?.preferences?.authentication?.connections || [];

  const content =
    isTenantLoading || isLoginLoading ? (
      <div className={classes.spinnerContainer}>
        <Progress />
      </div>
    ) : (
      <>
        <div className={clsx(classes.authBackground, classes.complete)} />
        <div className={classes.headingText}>Sign in to Atom</div>
        {emailPasswordError && (
          <div className={classes.errorText}>Invalid email or password</div>
        )}
        {authError && (
          <div className={classes.errorText}>
            Authentication failed. Please try again.
          </div>
        )}
        <div className={classes.textFieldContainer}>
          <div className={classes.textField}>
            <TextField
              variant="standard"
              type="email"
              value={loginForm.email}
              name="email"
              label="Email"
              onChange={onChange}
              onKeyDown={onKeyDown}
            />
          </div>
          <div className={clsx(classes.textField, 'fs-mask')}>
            <TextField
              variant="standard"
              type={isPasswordVisible ? 'text' : 'password'}
              value={loginForm.password}
              name="password"
              label="Password"
              onChange={onChange}
              onKeyDown={onKeyDown}
              style={styles.passwordField}
            />
            <div className={classes.passwordVisibleToggleContainer}>
              <IconButton onClick={togglePasswordVisibleState}>
                <Icon>remove_red_eye</Icon>
              </IconButton>
            </div>
          </div>
        </div>
        <ResetPasswordModal />
        <div className={classes.genericButtonContainer}>
          <button className={classes.genericButton} onClick={submit}>
            Sign In
          </button>
        </div>
        <div className={classes.registrationSocialLoginContainer}>
          <div className={classes.loginDecisionBreak}>
            <span>or</span>
          </div>
          <div className={classes.socialLoginContainer}>
            <GoogleSocialLoginButton text="Sign In with Google" />
          </div>
        </div>
        {connections.map(({ type, name }) => {
          switch (type) {
            case 'azure-ad': {
              return (
                <div className={classes.registrationSocialLoginContainer}>
                  <div className={classes.loginDecisionBreak}>
                    <span>or</span>
                  </div>
                  <a
                    href={activeDirectoryLoginUrl(name)}
                    className={classes.genericButtonSecondary}
                  >
                    Sign In with Active Directory
                  </a>
                </div>
              );
            }
            default: {
              return <div />;
            }
          }
        })}
      </>
    );

  return (
    <>
      <AppBar style={navBarStyle}>
        <Toolbar>
          <div>
            <Logo />
          </div>
          <Box sx={{ flexGrow: 1 }} />
          <div className={classes.navRightContainer}>
            <div className={classes.navRightText}>Don't have an account?</div>
            <div
              className={classes.navRightLogin}
              onClick={navigateToRegistration}
            >
              Sign Up
            </div>
          </div>
        </Toolbar>
      </AppBar>
      <div className={classes.container}>
        {content}
        <LoginFooter />
      </div>
    </>
  );
};

export default Login;
